import type { SurveyAnswer } from '@brighterly/lib-core-types/survey/interfaces'
import type { FeedbackSurveyType } from '../model/store/enums'

import { useToast } from '@shared/lib/toast'
import { useCsRoutes } from '@shared/api'

import { useFeedbackStore } from '../model/store'

interface FeedbackCreatePayload {
  lesson_feedback: {
    answers: SurveyAnswer[]
    source: string
  }
  survey_type: FeedbackSurveyType
}

interface FeedbackCreateApiOptions {
  onStart?: () => void
  onSuccess?: () => void
  onError?: () => void
}

export const useFeedbackCreateApi = async (options?: FeedbackCreateApiOptions): Promise<void> => {
  const { $axios } = useNuxtApp()

  const feedbackStore = useFeedbackStore()
  const toast = useToast()

  const bookingId = feedbackStore.getBooking?.id

  const setError = (msg: string): void => {
    toast.error(msg)
    options?.onError?.()
  }

  options?.onStart?.()

  if (!bookingId) {
    setError('Unexpected error. Invalid booking ID. Please try again.')
    return
  }

  const url = useCsRoutes('feedback.create', { '[booking_id]': bookingId })

  if (!url) {
    setError('Unexpected error. Invalid request URL. Please try again.')
    return
  }

  const payload: FeedbackCreatePayload = {
    lesson_feedback: {
      answers: feedbackStore.getFeedbackAnswers,
      source: 'CS',
    },
    survey_type: feedbackStore.getSurveyType,
  }

  try {
    await $axios.post(url, payload)
    options?.onSuccess?.()
  } catch {
    setError('Unexpected error. Failed to send feedback. Please try again.')
  }
}
